import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// Deep link query parameter names
export const DEEP_LINK_PARAMS = {
  TAB: 'tab',
  SERVICE_TYPE: 'serviceType',
  ORDER_ID: 'orderId',
  PROPOSAL_ID: 'proposalId',
} as const;

// Tab values
export type BuildingTab = 'services' | 'orders' | 'pending' | 'active';
export type ProviderTab = 'available' | 'applied';

// Deep link state for building view
export type BuildingDeepLinkState = {
  tab: BuildingTab | null;
  orderId: string | null;
  proposalId: string | null;
};

// Deep link state for provider view
export type ProviderDeepLinkState = {
  tab: ProviderTab | null;
  serviceType: string | null;
  orderId: string | null;
  proposalId: string | null;
};

// Highlight target info
export type HighlightTarget = {
  orderId: string | null;
  proposalId: string | null;
};

/**
 * Hook to read and consume deep link parameters from the URL
 * for the Services page navigation.
 */
export function useServiceDeepLink() {
  const [searchParams] = useSearchParams();
  
  // Track the last URL we processed to detect new deep links
  const lastProcessedUrlRef = useRef<string | null>(null);

  // Highlight state - persists until timeout
  const [highlightTarget, setHighlightTarget] = useState<HighlightTarget>({
    orderId: null,
    proposalId: null,
  });

  // Get current params as a stable string for comparison
  const currentParamsString = searchParams.toString();
  
  // Read current params
  const tab = searchParams.get(DEEP_LINK_PARAMS.TAB);
  const serviceType = searchParams.get(DEEP_LINK_PARAMS.SERVICE_TYPE);
  const orderId = searchParams.get(DEEP_LINK_PARAMS.ORDER_ID);
  const proposalId = searchParams.get(DEEP_LINK_PARAMS.PROPOSAL_ID);

  // Check if this is a new deep link we haven't processed yet
  const hasDeepLinkParams = !!(tab || serviceType || orderId || proposalId);
  const isNewDeepLink = hasDeepLinkParams && currentParamsString !== lastProcessedUrlRef.current;
  
  // Debug logging
  if (hasDeepLinkParams) {
    console.log('[useServiceDeepLink] Params detected:', { tab, serviceType, orderId, proposalId });
    console.log('[useServiceDeepLink] isNewDeepLink:', isNewDeepLink, '(current:', currentParamsString, ', lastProcessed:', lastProcessedUrlRef.current, ')');
  }

  // Get deep link params (for immediate use)
  const getDeepLinkParams = useCallback(() => {
    return { tab, serviceType, orderId, proposalId };
  }, [tab, serviceType, orderId, proposalId]);

  // Mark this deep link as handled (without mutating the URL, so links remain shareable)
  const markDeepLinkHandled = useCallback(() => {
    lastProcessedUrlRef.current = currentParamsString;
  }, [currentParamsString]);

  // Set highlight target from current URL params
  const setHighlightFromParams = useCallback(() => {
    if (orderId || proposalId) {
      setHighlightTarget({
        orderId: orderId,
        proposalId: proposalId,
      });
    }
  }, [orderId, proposalId]);

  // Clear the highlight
  const clearHighlight = useCallback(() => {
    setHighlightTarget({ orderId: null, proposalId: null });
  }, []);

  // Scroll to the target element
  const scrollToTarget = useCallback((elementId: string, delay = 100) => {
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, delay);
  }, []);

  // Check if an order should be highlighted
  const isOrderHighlighted = useCallback((checkOrderId: string) => {
    return highlightTarget.orderId === checkOrderId;
  }, [highlightTarget.orderId]);

  // Check if a proposal should be highlighted
  const isProposalHighlighted = useCallback((checkProposalId: string) => {
    return highlightTarget.proposalId === checkProposalId;
  }, [highlightTarget.proposalId]);

  // Auto-clear highlight after timeout
  useEffect(() => {
    if (highlightTarget.orderId || highlightTarget.proposalId) {
      const timer = setTimeout(() => {
        clearHighlight();
      }, 10000); // Clear highlight after 10 seconds

      return () => clearTimeout(timer);
    }
  }, [highlightTarget, clearHighlight]);

  return {
    // Current params (read on every call)
    tab,
    serviceType,
    orderId,
    proposalId,
    // Flags
    hasDeepLinkParams,
    isNewDeepLink,
    // Methods
    getDeepLinkParams,
    markDeepLinkHandled,
    setHighlightFromParams,
    // Highlight state
    highlightTarget,
    clearHighlight,
    scrollToTarget,
    isOrderHighlighted,
    isProposalHighlighted,
  };
}

/**
 * Helper function to build navigation URL with deep link params.
 * Used by SocketContext to create proper navigation URLs.
 */
export function buildServiceDeepLink(params: {
  tab?: string;
  serviceType?: string;
  orderId?: string;
  proposalId?: string;
}): string {
  // If we have an orderId, link directly to the order detail page
  if (params.orderId) {
    return `/services/order/${params.orderId}`;
  }
  // Otherwise fall back to the services list with query params
  const searchParams = new URLSearchParams();

  if (params.tab) searchParams.set(DEEP_LINK_PARAMS.TAB, params.tab);
  if (params.serviceType) searchParams.set(DEEP_LINK_PARAMS.SERVICE_TYPE, params.serviceType);
  if (params.proposalId) searchParams.set(DEEP_LINK_PARAMS.PROPOSAL_ID, params.proposalId);

  const queryString = searchParams.toString();
  return queryString ? `/services?${queryString}` : '/services';
}
